/**
 * Supabase backend adapter.
 *
 * This is the ONLY application-level adapter that selects the current
 * Supabase-backed implementation. Feature code must consume src/backend
 * instead of importing Supabase directly.
 *
 * Replacing Supabase later means implementing the same provider surface in
 * another adapter and selecting it in src/backend/index.js/configuration.
 */
import { base44 } from '@/api/standaloneClient';
import { supabase, supabaseConfigured } from '@/lib/supabaseClient';
import {
  ensureCloudProfile,
  getCloudProfile,
  getCloudUser,
  getPendingSync,
  isCloudEnabled,
  pushRecord,
  pullRecords,
  pullOrganisationRecords,
  syncFromCloud,
  syncNow,
  flushSyncQueue,
} from '@/lib/cloudSync';
import {
  getDeviceIdentity,
  registerDeviceSession,
  heartbeatDeviceSession,
  startDeviceHeartbeat,
} from '@/lib/deviceRegistry';

const authClient = Object.freeze({
  configured: supabaseConfigured,
  getSession: (...args) => supabase?.auth.getSession(...args),
  onAuthStateChange: (...args) => supabase?.auth.onAuthStateChange(...args),
  exchangeCodeForSession: (...args) => supabase?.auth.exchangeCodeForSession(...args),
  signOut: (...args) => supabase?.auth.signOut(...args),
});

export const backend = Object.freeze({
  kind: 'supabase',
  entities: base44.entities,
  auth: base44.auth,
  functions: base44.functions,
  integrations: base44.integrations,
  db: base44,
  authClient,
  cloud: Object.freeze({
    isEnabled: isCloudEnabled,
    ensureProfile: ensureCloudProfile,
    getProfile: getCloudProfile,
    getUser: getCloudUser,
    getPendingSync,
    pushRecord,
    pullRecords,
    pullOrganisationRecords,
    syncFromCloud,
    syncNow,
    flushSyncQueue,
  }),
  device: Object.freeze({
    getIdentity: getDeviceIdentity,
    registerSession: registerDeviceSession,
    heartbeatSession: heartbeatDeviceSession,
    startHeartbeat: startDeviceHeartbeat,
  }),
});
